/**
 * 预订人模块
 */
import React, { memo, useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import cls from 'classnames';
import { Input, Radio, RadioChangeEvent } from 'antd';
import intl from 'react-intl-universal';

import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import BaseMobileInput from '@/components/BaseMobileInput';
import BaseEmailInput from '@/components/BaseEmailInput';
import baseService from '@/api/checkInBase';
import { MemberLevel, ContractType } from '@/config/constant';
import { formatAreaMobileStr, parseAreaMobileStr } from '@/components/BaseMobileInput/helper';

import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  handleCustomerChange,
  handleMemberStatusChange,
} from '@/redux/actions/reservation/createReserve';
import { DetectMethod, ReducerState, storeName } from '@/redux/reducers/reservation/createReserve';
import { MemberLevelItem } from '@/redux/reducers/guest';
import { isNil } from '@/utils/number';
import IntlContext from '@/components/Intl/IntlContext';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState, globalStore) => {
  const { commonInfo, customerInfo } = state;
  const { memberLevelMap } = globalStore.guest;
  return { customerInfo, memberLevel: commonInfo.memberLevel, memberLevelMap };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callSetCustomerData: handleCustomerChange,
  callMemberStatusChange: handleMemberStatusChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    customerInfo,
    memberLevel,
    memberLevelMap,
    callSetCustomerData,
    callMemberStatusChange,
  } = props;

  const intlCtx = useContext(IntlContext);

  // 查询会员信息
  const { getMemberInfoByMobile, getMemberInfoByEmail } = useMemo(() => {
    const defaultType = intlCtx.TenantSysConfig.contractType;
    let timer;

    // 更新会员信息
    const getMemberInfo = async (data) => {
      const currentToken = timer;
      try {
        const res = await baseService.getInfoByPhone(data);
        const resData = res.data || {};
        if (currentToken !== timer) return;
        callSetCustomerData({ wyuId: resData.wyuid, vipDetectMethod: DetectMethod.Auto });
        callMemberStatusChange(
          !isNil(resData.memberLevel) ? resData.memberLevel : MemberLevel.Common,
        );
      } catch (_) {
        if (currentToken !== timer) return;
        // 接口出错 清空id
        callSetCustomerData({ wyuId: undefined });
        callMemberStatusChange(MemberLevel.Common);
      }
    };

    return {
      getMemberInfoByMobile: (val) => {
        if (defaultType === ContractType.MOBILE) {
          if (timer) {
            window.clearTimeout(timer);
            timer = null;
          }
          timer = window.setTimeout(() => {
            const { mobileStr } = parseAreaMobileStr(val);
            if (mobileStr) {
              getMemberInfo({ mobile: val });
            }
          }, 500);
        }
      },
      getMemberInfoByEmail: (val) => {
        if (defaultType === ContractType.EMAIL) {
          if (timer) {
            window.clearTimeout(timer);
            timer = null;
          }
          if (val) {
            timer = window.setTimeout(() => {
              getMemberInfo({ email: val });
            }, 500);
          }
        }
      },
    };
  }, [intlCtx.TenantSysConfig.contractType]);

  // 预订人姓名变更
  const handleNameChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value || '';
    // if (value.length > 32) return;
    callSetCustomerData({ name: value });
  };

  // 预订人手机号变更
  const handlePhoneChange = (mobile: string) => {
    callSetCustomerData({ mobile });
    getMemberInfoByMobile(mobile);
  };

  // 预订人OTA虚拟号变更
  const handleOTANoChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    callSetCustomerData({ otaOrderNo: event.target.value });
  };

  // 预订人邮箱变更
  const handleEmailChange = (email: string) => {
    callSetCustomerData({ email });
    getMemberInfoByEmail(email);
  };

  // 会员状态改变
  const handleToggleMemberStatus: (event: RadioChangeEvent) => void = (event) => {
    callSetCustomerData({ vipDetectMethod: DetectMethod.Manual });
    callMemberStatusChange(event.target.value);
  };

  const detectTag = (
    <span className={cls(styles.detectFlagTag, 'detect-tag')}>
      {intl.get(
        customerInfo.vipDetectMethod === DetectMethod.Manual
          ? 'reservation_booker_level_detect_manual'
          : 'reservation_booker_level_detect_auto',
      )}
    </span>
  );

  return (
    <ContentBlock
      title={intl.get('reservation_booker_block_title')}
      className={styles.customerInfoContainer}
    >
      <FormItem label={intl.get('app_constants_booker_name')}>
        <Input
          placeholder={intl.get('reservation_booker_field_name_placeholder')}
          maxLength={32}
          value={customerInfo.name}
          onChange={handleNameChange}
        />
      </FormItem>
      <FormItem label={intl.get('app_reservation_phone_number')}>
        <BaseMobileInput
          inputProps={{
            placeholder: intl.get('reservation_booker_field_mobile_placeholder'),
          }}
          value={customerInfo.mobile}
          onChange={handlePhoneChange}
        />
      </FormItem>
      <FormItem label={intl.get('reservation_booker_field_ota_virtual_label')}>
        <Input
          placeholder={intl.get('reservation_booker_field_ota_virtual_placeholder')}
          value={customerInfo.otaOrderNo}
          onChange={handleOTANoChange}
          maxLength={20}
        />
      </FormItem>
      <FormItem label={intl.get('help_store_address_type_email')}>
        <BaseEmailInput
          placeholder={intl.get('reservation_booker_field_email_placeholder')}
          value={customerInfo.email}
          onChange={handleEmailChange}
        />
      </FormItem>
      <FormItem
        label={intl.get('app_reservation_member_level')}
        className={styles.memberStatusItem}
      >
        <Radio.Group value={memberLevel} onChange={handleToggleMemberStatus}>
          {Object.values(memberLevelMap)?.map((item: MemberLevelItem) => (
            <Radio key={item.id} value={item.level}>
              <span>{item.name}</span>
              {detectTag}
            </Radio>
          ))}
        </Radio.Group>
      </FormItem>
    </ContentBlock>
  );
};

const getDefaultValue = (val) => val || '—';

export const ReadOnlyInfo: React.FC<MapStateProps> = (props) => {
  const { customerInfo, memberLevel, memberLevelMap } = props;

  return (
    <ContentBlock
      title={intl.get('reservation_booker_block_title')}
      className={styles.readonlyCustomer}
    >
      <FormItem label={intl.get('app_constants_booker_name')}>
        {getDefaultValue(customerInfo.name)}
      </FormItem>
      <FormItem label={intl.get('app_reservation_phone_number')}>
        {formatAreaMobileStr(customerInfo.mobile)}
      </FormItem>
      <FormItem label={intl.get('reservation_booker_field_ota_virtual_label')}>
        {getDefaultValue(customerInfo.otaOrderNo)}
      </FormItem>
      <FormItem label={intl.get('help_store_address_type_email')}>
        {getDefaultValue(customerInfo.email)}
      </FormItem>
      <FormItem label={intl.get('app_reservation_member_level')}>
        <Radio.Group value={memberLevel} disabled>
          {Object.values(memberLevelMap)?.map((item: MemberLevelItem) => (
            <Radio key={item.id} value={item.level}>
              <span>{item.name}</span>
            </Radio>
          ))}
        </Radio.Group>
      </FormItem>
    </ContentBlock>
  );
};

export const ReadOnlyCustomer = connect<MapStateProps>(mapStateToProps)(memo(ReadOnlyInfo));

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
